"use client";

import { useTranslations } from "next-intl";
import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import IconSpinnerDots from "@/components/icon/spinner/IconSpinnerDots";
import { AnimatedState } from "@/components/flow/AnimatedState";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MarketRequestForm } from "@/lib/types/resource";
import { Textarea } from "@/components/ui/textarea";
import { onSubmitMarketRequest } from "../../request/actions";
import { toast } from "sonner";
type Props = {};

const getDefaultForm = (): MarketRequestForm => ({
  title: "",
  description: "",
  locations: "",
  email: "",
});

const MarketRequestSheet: React.FC<Props> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<MarketRequestForm>(getDefaultForm());
  const t = useTranslations("market");

  const { isPending, mutateAsync } = useMutation({
    mutationFn: onSubmitMarketRequest.bind(null, form),
  });

  const onSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await mutateAsync();

      toast.success(t("request.sheet.alert.success"));
      setForm(getDefaultForm());
      setIsOpen(false);
    } catch (error) {
      toast.error(t("request.sheet.alert.error"));
      setIsOpen(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline">{t("request.cta.button")}</Button>
      </SheetTrigger>
      <SheetContent side="right" className=" w-full sm:w-3/4 overflow-y-scroll">
        <SheetHeader>
          <SheetTitle>{t("request.sheet.title")}</SheetTitle>
          <SheetDescription className="text-xs">
            {t("request.sheet.description")}
          </SheetDescription>
        </SheetHeader>
        <form className="flex flex-col gap-4 pt-6" onSubmit={onSubmitHandler}>
          <FormInputWrapper
            label={t("request.sheet.form.title.label")}
            for="market-title"
            description={t("request.sheet.form.title.description")}
          >
            <Input
              id="market-title"
              placeholder={t("request.sheet.form.title.placeholder")}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="placeholder:text-xs"
            />
          </FormInputWrapper>
          <FormInputWrapper
            label={t("request.sheet.form.description.label")}
            for="market-description"
            description={t("request.sheet.form.description.description")}
          >
            <Textarea
              id="market-description"
              placeholder={t("request.sheet.form.description.placeholder")}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="placeholder:text-xs"
              rows={4}
            />
          </FormInputWrapper>

          <FormInputWrapper
            label={t("request.sheet.form.locations.label")}
            for="market-locations"
            description={t("request.sheet.form.locations.description")}
          >
            <Input
              id="market-locations"
              placeholder={t("request.sheet.form.locations.placeholder")}
              value={form.locations}
              onChange={(e) => setForm({ ...form, locations: e.target.value })}
              className="placeholder:text-xs"
            />
          </FormInputWrapper>

          <FormInputWrapper
            label={t("request.sheet.form.email.label")}
            for="market-email"
            description={t("request.sheet.form.email.description")}
          >
            <Input
              id="market-email"
              placeholder={t("request.sheet.form.email.placeholder")}
              value={form.email}
              type="email"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="placeholder:text-xs"
            />
          </FormInputWrapper>

          <div className="flex flex-row gap-2 items-center justify-around">
            <Button
              variant="outline"
              type="button"
              className="w-full"
              onClick={() => setIsOpen(false)}
            >
              {t("request.sheet.button.cancel")}
            </Button>
            <Button type="submit" className="w-full" disabled={isPending}>
              <AnimatedState>
                {isPending ? (
                  <IconSpinnerDots />
                ) : (
                  t("request.sheet.button.submit")
                )}
              </AnimatedState>
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default MarketRequestSheet;

type FormInputWrapperProps = {
  label: string;
  for: string;
  description: string;
  children: React.ReactNode;
};

const FormInputWrapper: React.FC<FormInputWrapperProps> = ({
  label,
  description,
  for: htmlFor,
  children,
}) => {
  return (
    <div className="space-y-1">
      <Label className="font-medium" htmlFor={htmlFor}>
        {label}
      </Label>
      {children}
      <p className="text-muted-foreground text-[10px] pt-1 leading-3">
        {description}
      </p>
    </div>
  );
};
